import { useState } from 'react';
import { api, type Category, type LineItem, type Receipt } from '../lib/api';
import { MoneyInput } from '../components/MoneyInput';
import { CategorySheet } from '../components/CategorySheet';
import { ReceiptField } from '../components/ReceiptField';
import { ErrorNote, Sheet } from '../components/ui';

/**
 * Field order is deliberate: Amount -> Date -> Category -> Merchant -> Notes.
 *
 * Amount comes first because it is the only value you cannot reconstruct
 * later from the receipt photo, and because autofocusing it means the numeric
 * keypad is already up when the sheet opens. Everything below it can be fixed
 * at a desk; the amount is why you are holding the phone.
 */
export function LineSheet({
  open, onClose, reportId, categories, receipts, existing, attachReceiptId, onSaved,
}: {
  open: boolean;
  onClose: () => void;
  reportId: string;
  categories: Category[];
  receipts: Receipt[];
  existing?: LineItem | null;
  attachReceiptId?: string | null;
  onSaved: () => void | Promise<void>;
}) {
  const expenseCategories = categories.filter((c) => c.appliesToKind === 'expense');

  const [amount, setAmount] = useState(existing?.amount ?? '');
  const [date, setDate] = useState(existing?.expense_date ?? new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState<Category | null>(
    existing ? expenseCategories.find((c) => c.id === existing.category_id) ?? null : null,
  );
  const [merchant, setMerchant] = useState(existing?.merchant ?? '');
  // A receipt can be attached now, swapped, or removed -- and it is tracked
  // separately from the line so a receipt captured here survives a category
  // change or a validation bounce.
  const [receiptId, setReceiptId] = useState<string | null>(
    existing?.receipt_id ?? attachReceiptId ?? null,
  );
  const [notes, setNotes] = useState(existing?.notes ?? '');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Two-step confirm rather than window.confirm(): a native dialog on a phone
  // is easy to dismiss by accident, and deleting a line the person just typed
  // in is annoying enough to be worth one deliberate tap.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  function reset() {
    setAmount('');
    setMerchant('');
    setNotes('');
    setError(null);
    setConfirmingDelete(false);
    setReceiptId(null);
  }

  async function remove() {
    if (!existing) return;
    setBusy(true);
    setError(null);
    try {
      await api.delete(`/line-items/${existing.id}`);
      await onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete that line');
    } finally {
      setBusy(false);
      setConfirmingDelete(false);
    }
  }

  async function save(addAnother: boolean) {
    if (!amount || Number(amount) <= 0) { setError('Enter an amount'); return; }
    if (!category) { setError('Choose a category'); return; }

    setBusy(true);
    setError(null);
    try {
      const body = {
        expenseDate: date,
        categoryId: category.id,
        amount: Number(amount).toFixed(2),
        merchant: merchant || undefined,
        notes: notes || undefined,
        // Always sent, including as null: that is what makes "Remove" stick.
        receiptId,
      };

      if (existing) await api.patch(`/line-items/${existing.id}`, body);
      else await api.post(`/reports/${reportId}/line-items`, body);

      // Awaited, not fired and forgotten. Closing before the reload lands
      // leaves the report holding pre-save data for a moment, and anything
      // that opens an editor in that window seeds itself from it -- which is
      // how a detached receipt kept reappearing in the editor while the card
      // below correctly said there was none.
      await onSaved();
      if (addAnother) reset();
      else onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        title={existing ? 'Edit expense' : 'Add expense'}
        footer={
          <div className="space-y-2">
            <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void save(false)}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            {!existing ? (
              // Month end is eight receipts in a row; this is the difference
              // between one sitting and eight.
              <button type="button" className="btn-secondary w-full" disabled={busy} onClick={() => void save(true)}>
                Save &amp; add another
              </button>
            ) : confirmingDelete ? (
              <div className="flex gap-2">
                <button type="button" className="btn-secondary flex-1" disabled={busy}
                        onClick={() => setConfirmingDelete(false)}>
                  Keep it
                </button>
                <button type="button" className="btn-danger flex-1" disabled={busy}
                        onClick={() => void remove()}>
                  {busy ? 'Deleting…' : 'Yes, delete'}
                </button>
              </div>
            ) : (
              <button type="button" className="btn-danger w-full" disabled={busy}
                      onClick={() => setConfirmingDelete(true)}>
                Delete this expense
              </button>
            )}
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="amount">Amount</label>
            <MoneyInput id="amount" value={amount} onChange={setAmount} autoFocus={!existing} />
          </div>

          <div>
            <label className="label" htmlFor="date">Date</label>
            <input id="date" type="date" className="field" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>

          <div>
            <span className="label">Category</span>
            <button type="button" onClick={() => setPickerOpen(true)}
                    className="field flex items-center justify-between text-left">
              <span className={category ? '' : 'text-slate-400'}>
                {category ? category.name : 'Choose a category'}
              </span>
              <span className="text-slate-400">{category?.glCode ?? '›'}</span>
            </button>
          </div>

          <div>
            <label className="label" htmlFor="merchant">Merchant</label>
            <input id="merchant" className="field" value={merchant} placeholder="Where was it spent?"
                   onChange={(e) => setMerchant(e.target.value)} />
          </div>

          <ReceiptField
            reportId={reportId}
            receipts={receipts}
            value={receiptId}
            onChange={setReceiptId}
            requiredHint={Boolean(category?.requiresReceipt) && !receiptId}
          />

          <div>
            <label className="label" htmlFor="notes">Notes (optional)</label>
            <textarea id="notes" className="field" rows={2} value={notes}
                      onChange={(e) => setNotes(e.target.value)} />
          </div>

          {confirmingDelete ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              Delete this expense? The receipt stays on the report and can be attached to another line.
            </p>
          ) : null}

          <ErrorNote>{error}</ErrorNote>
        </div>
      </Sheet>

      <CategorySheet
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        categories={expenseCategories}
        currentId={category?.id}
        onPick={setCategory}
      />
    </>
  );
}
